import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { useAuth } from '../../hooks/useAuth';
import { isSuperAdmin } from '../../utils/roles';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

interface FeatureRow {
  feature_key: string;
  display_name: string;
  description: string | null;
  category: string;
  meter_key: string | null;
  default_billing_model: string;
  default_price_per_unit_cents: number;
  unit_label: string | null;
  is_premium: boolean;
  default_enabled?: boolean;
}

interface OverrideRow {
  feature_key: string;
  enabled: boolean;
  price_per_unit_cents?: number;
}

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}`,
  };
}

export const FeatureToggleList: React.FC<{ entityType: 'dealer' | 'customer' | 'property'; entityId: string }> = ({ entityType, entityId }) => {
  const { user } = useAuth();
  const isSuper = isSuperAdmin((user?.role ?? '') as any);
  const [registry, setRegistry] = useState<FeatureRow[]>([]);
  const [overrides, setOverrides] = useState<Record<string, OverrideRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [regRes, ovRes] = await Promise.all([
        fetch(`${API}/stripe/admin/billing-features`, { headers: authHeaders() }),
        fetch(`${API}/features?entity_type=${entityType}&entity_id=${entityId}`, { headers: authHeaders() }),
      ]);
      const reg = (await regRes.json()) as FeatureRow[];
      const ov  = (await ovRes.json())  as OverrideRow[];
      setRegistry(Array.isArray(reg) ? reg : []);
      const map: Record<string, OverrideRow> = {};
      for (const r of (Array.isArray(ov) ? ov : [])) map[r.feature_key] = r;
      setOverrides(map);
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { if (entityId) load(); }, [entityType, entityId]);

  const effectiveEnabled = (f: FeatureRow): boolean => {
    const o = overrides[f.feature_key];
    if (o) return o.enabled;
    return f.default_enabled !== false;
  };

  const toggle = async (f: FeatureRow) => {
    if (!isSuper) return;
    const next = !effectiveEnabled(f);
    setPending((p) => ({ ...p, [f.feature_key]: true }));
    try {
      const res = await fetch(`${API}/features/${f.feature_key}/toggle`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId, enabled: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? data.detail ?? `toggle ${res.status}`);
      }
      await load();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setPending((p) => { const n = { ...p }; delete n[f.feature_key]; return n; });
    }
  };

  if (loading) return <div className="text-sm text-gray-500 p-4">Loading features...</div>;

  // Group by category
  const byCategory: Record<string, FeatureRow[]> = {};
  for (const f of registry) (byCategory[f.category ?? 'other'] ??= []).push(f);

  return (
    <Card padding={false}>
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Feature billing</h3>
          <p className="text-sm text-gray-500">
            Toggle metered features on/off for this {entityType}. Inherits from registry default unless overridden here.
            {!isSuper && ' Read-only for your role.'}
          </p>
        </div>
      </div>
      {error && (
        <div className="mx-4 mt-3 px-3 py-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}
      <div className="p-4 space-y-6">
        {Object.entries(byCategory).map(([cat, feats]) => (
          <div key={cat}>
            <div className="text-xs uppercase font-semibold text-gray-500 mb-2">{cat}</div>
            <div className="border rounded divide-y">
              {feats.map((f) => {
                const enabled = effectiveEnabled(f);
                const isOverride = !!overrides[f.feature_key];
                const isPending = !!pending[f.feature_key];
                return (
                  <div key={f.feature_key} className="px-3 py-2 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {f.display_name}
                        {f.is_premium && <Badge color="purple">premium</Badge>}
                        {isOverride && <Badge color="blue">override</Badge>}
                      </div>
                      {f.description && <div className="text-xs text-gray-500">{f.description}</div>}
                      <div className="text-xs text-gray-400 mt-0.5">
                        {f.meter_key ? `meter: ${f.meter_key}` : 'licensed'}
                        {f.default_price_per_unit_cents > 0 && f.unit_label
                          ? ` · default $${(f.default_price_per_unit_cents/100).toFixed(2)}/${f.unit_label}`
                          : ''}
                      </div>
                    </div>
                    <div className="ml-4">
                      {isSuper ? (
                        <button
                          onClick={() => toggle(f)}
                          disabled={isPending}
                          className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${enabled ? 'bg-green-500' : 'bg-gray-300'} ${isPending ? 'opacity-50' : ''}`}
                          aria-label={`Toggle ${f.display_name}`}
                        >
                          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
                        </button>
                      ) : (
                        <Badge color={enabled ? 'green' : 'gray'}>{enabled ? 'on' : 'off'}</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
